/**
 * Lightweight Turbo helper utilities
 * Replaces the bloated turbo_events.js with essential functions only
 */

/**
 * Execute callback when DOM is ready (Turbo-compatible)
 * @param {Function} callback - Function to execute when ready
 */
export function onReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('turbo:load', callback, { once: true })
  } else {
    callback()
  }
}

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 */
export function debounce(func, wait = 300) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Throttle function for scroll/resize events
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 */
export function throttle(func, limit = 100) {
  let inThrottle
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * Check if Turbo is enabled
 * @returns {boolean}
 */
export function isTurboEnabled() {
  return typeof window.Turbo !== 'undefined' && window.Turbo.session.drive
}

/**
 * Safely navigate with Turbo
 * @param {string} url - URL to navigate to
 */
export function turboVisit(url) {
  if (isTurboEnabled()) {
    window.Turbo.visit(url)
  } else {
    window.location.href = url
  }
}

/**
 * Replace content with Turbo Stream
 * @param {string} target - Target element ID
 * @param {string} html - HTML content to insert
 */
export function turboReplace(target, html) {
  const element = document.getElementById(target)
  if (element && window.Turbo) {
    element.innerHTML = html
  }
}

// Export all helpers
export default {
  onReady,
  debounce,
  throttle,
  isTurboEnabled,
  turboVisit,
  turboReplace
}
